/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { withRouter, useParams, useHistory } from "react-router-dom";
import Page from "components/common_components/Page/Page";
import { useDispatch } from "react-redux";
import { notifyError, notifySuccess, notifyWarning } from "redux/actions/Notifications";
import LoadingPage from "components/common_components/LoadingPage";
import Form from "./Form";
import CustomersContext from "./CustomersContext";
import { getDateMillis } from "utils/dateUtils";
import Header from "components/common_components/Header";
import { ConfirmDialog } from "components/common_components/Dialogs";

function Customer(props) {
    const history = useHistory();
    const { id } = useParams();
    const [customer, setCustomer] = useState(undefined);
    const [resources, setResources] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();
    const { mode } = props;
    const [confirmDialogProps, setConfirmDialogProps] = useState({
        open: false,
        title: "",
        content: "",
        primaryButtonText: "",
        onPrimaryClick: undefined,
        secondaryButtonText: "Cancel",
        onSecondaryClick: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
        onClose: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
    });

    const { Customers, Storage } = useContext(CustomersContext);

    function getCustomer() {
        setIsLoading(true);
        Customers.getOneById({
            id,
            onSuccess: async (data) => {
                let photo = null;
                try {
                    photo = await Storage.downloadAsync({
                        dirName: "management/clients/images",
                        fileName: `client_id_${data.id}`,
                    });
                } catch (message) {}
                setCustomer({ ...data, photo });
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsLoading(false);
            },
        });
    }

    function getResources() {
        Customers.getResources({
            onSuccess: (data) => {
                setResources(data);
            },
            onError: (error) => {
                setResources(undefined);
                dispatch(notifyError(error));
            },
        });
    }

    useEffect(() => {
        if (id) {
            getCustomer();
        }
    }, [id]);

    useEffect(() => {
        getResources();
    }, []);

    function onEditHandler() {
        history.push(`/customers/${id}/update`);
    }

    function onCancelHandler() {
        history.goBack();
    }

    async function onSuccess(clientId, setSubmitting, payload) {
        const { photo } = payload;
        try {
            if (photo) {
                await Storage.uploadAsync({
                    path: `management/clients/images/client_id_${clientId}`,
                    file: photo,
                });
            }
            dispatch(notifySuccess(`Client ${mode === "add" ? "created" : "updated"} successfully!`));
        } catch (error) {
            dispatch(notifyWarning(`Client ${mode === "add" ? "created" : "updated"} successfully!`));
            dispatch(notifyWarning(`Error uploading photo! ${error.message}`));
        } finally {
            setSubmitting(false);
            onCancelHandler();
        }
    }

    function onError(error, setSubmitting, setErrors) {
        if (error && error.errors) {
            setErrors(error.errors);
            dispatch(notifyError(error.message));
        } else {
            onCancelHandler();
            dispatch(notifyError(error));
        }
        setSubmitting(false);
    }

    function onSubmitHandler(values, { setSubmitting, setErrors }) {
        const { ssn, birthDate, email, country: fullCountry, enabled, ...payload } = values;

        const birthDateInMillis = getDateMillis(birthDate);

        const apiCall = mode === "add" ? Customers.add : Customers.update;

        const execute = () => {
            setSubmitting(true);
            apiCall({
                payload: {
                    id: mode === "update" ? id : null,
                    ...payload,
                    birthDate: birthDateInMillis,
                    ssn: ssn.trim().toLowerCase(),
                    email: email.trim().toLowerCase(),
                    country: fullCountry ? fullCountry.code : null,
                    enabled: mode === "add" ? true : enabled,
                },
                onSuccess: (response) => onSuccess(response, setSubmitting, payload),
                onError: (error) => onError(error, setSubmitting, setErrors),
            });
        };

        if (mode === "add") {
            execute();
        } else if (mode === "update") {
            if (values.homePhone !== customer.homePhone) {
                setConfirmDialogProps((prev) => ({
                    ...prev,
                    open: true,
                    title: "Modify home phone",
                    content:
                        "Are you sure you want to change the Home Phone? \nActive Chat Conversations will be closed and a new one will be created.",
                    primaryButtonText: "Yes, continue",
                    onPrimaryClick: () => {
                        execute();
                        prev.onClose();
                    },
                }));
            } else {
                execute();
            }
        }
    }

    return (
        <Page>
            <Header title={(customer && customer.fullName) || "Client"} />
            {isLoading && <LoadingPage />}
            {!isLoading && (
                <Form
                    customer={customer}
                    resources={resources}
                    {...props}
                    onCancel={onCancelHandler}
                    onSubmit={onSubmitHandler}
                    onEdit={onEditHandler}
                />
            )}
            <ConfirmDialog {...confirmDialogProps} />
        </Page>
    );
}

Customer.propTypes = {
    mode: PropTypes.oneOf(["add", "detail", "update"]).isRequired,
    title: PropTypes.string,
};

export default withRouter(Customer);
